import * as React from 'react'
import { Info, Lightbulb, AlertTriangle } from 'lucide-react'

import { cn } from '@/lib/utils'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { Table, TableBody, TableCell, TableRow } from '@/components/ui/table'
import { H1, H2, Lead, Prose } from '@/components/docs/prose'

// ---------- PageMeta ----------

interface PageMetaProps {
  title: string
  summary?: string
}

export function PageMeta({ title, summary }: PageMetaProps) {
  return (
    <div className="mb-10">
      <H1 className="mb-3">{title}</H1>
      {summary && <Lead className="mb-6">{summary}</Lead>}
      <Separator />
    </div>
  )
}

// ---------- DocSection ----------

interface DocSectionProps {
  id: string
  title: string
  className?: string
  children?: React.ReactNode
}

export function DocSection({ id, title, className, children }: DocSectionProps) {
  return (
    <section id={id} className={cn('mb-12', className)}>
      <H2>{title}</H2>
      <Prose>{children}</Prose>
    </section>
  )
}

// ---------- HowToUse ----------

interface AccentSectionProps {
  children?: React.ReactNode
  className?: string
}

export function HowToUse({ children, className }: AccentSectionProps) {
  return (
    <section id="how-to-use" className={cn('mb-12', className)}>
      <H2>How to use</H2>
      <div className="border-l-2 pl-5 border-foreground/20">
        <Prose>{children}</Prose>
      </div>
    </section>
  )
}

// ---------- BehindScenes ----------

export function BehindScenes({ children, className }: AccentSectionProps) {
  return (
    <section id="behind-the-scenes" className={cn('mb-12', className)}>
      <H2>Behind the scenes</H2>
      <div className="border-l-2 border-dashed pl-5 border-foreground/20">
        <Prose>{children}</Prose>
      </div>
    </section>
  )
}

// ---------- StepList ----------

interface StepItem {
  title: string
  body: string
}

interface StepListProps {
  items: StepItem[]
  className?: string
}

export function StepList({ items, className }: StepListProps) {
  return (
    <div className={cn('space-y-6 mb-4', className)}>
      {items.map((item, index) => (
        <div key={index} className="flex gap-4">
          <span className="shrink-0 tabular-nums text-xs font-semibold text-muted-foreground mt-0.5 w-6">
            {String(index + 1).padStart(2, '0')}
          </span>
          <div>
            <p className="text-[15px] font-semibold text-foreground mb-1">{item.title}</p>
            <p className="text-[15px] leading-[1.75] text-foreground/85">{item.body}</p>
          </div>
        </div>
      ))}
    </div>
  )
}

// ---------- KVTable ----------

interface KVRow {
  key: string
  value: string
}

interface KVTableProps {
  rows: KVRow[]
  className?: string
}

export function KVTable({ rows, className }: KVTableProps) {
  return (
    <div className={cn('mb-4', className)}>
      <Table>
        <TableBody>
          {rows.map((row, index) => (
            <TableRow key={index}>
              <TableCell className="w-44 text-muted-foreground text-sm py-2.5 pl-0">{row.key}</TableCell>
              <TableCell className="text-sm py-2.5">{row.value}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}

// ---------- Callout ----------

type CalloutVariant = 'info' | 'tip' | 'warn'

interface CalloutProps {
  variant?: CalloutVariant
  children?: React.ReactNode
  className?: string
}

const calloutConfig: Record<CalloutVariant, { icon: React.ElementType; border: string; bg: string }> = {
  info: { icon: Info, border: 'border-foreground/20', bg: '' },
  tip: { icon: Lightbulb, border: 'border-foreground/40', bg: '' },
  warn: { icon: AlertTriangle, border: 'border-destructive/50', bg: 'bg-destructive/5' },
}

export function Callout({ variant = 'info', children, className }: CalloutProps) {
  const { icon: Icon, border, bg } = calloutConfig[variant]
  return (
    <div className={cn('flex gap-3 rounded-md border p-4 text-sm mb-4', border, bg, className)}>
      <Icon className="h-4 w-4 shrink-0 mt-0.5 text-foreground/70" aria-hidden="true" />
      <div className="text-foreground/85 leading-[1.6]">{children}</div>
    </div>
  )
}

// ---------- Flow ----------

interface FlowProps {
  steps: string[]
  className?: string
}

export function Flow({ steps, className }: FlowProps) {
  return (
    <div className={cn('mb-4', className)} role="list">
      {steps.map((step, index) => (
        <React.Fragment key={index}>
          <div role="listitem" className="rounded-md border border-border bg-card px-4 py-2 text-sm text-foreground">
            {step}
          </div>
          {index < steps.length - 1 && (
            <div aria-hidden="true" className="flex justify-center py-1 text-muted-foreground/50 text-xs">
              &#8595;
            </div>
          )}
        </React.Fragment>
      ))}
    </div>
  )
}

// ---------- Tag ----------

type TagVariant = 'model' | 'endpoint' | 'page' | 'default'

interface TagProps {
  variant?: TagVariant
  children?: React.ReactNode
  className?: string
}

export function Tag({ variant = 'default', children, className }: TagProps) {
  const monoClass = 'font-mono text-[11px]'
  return (
    <Badge
      variant="outline"
      className={cn(
        (variant === 'model' || variant === 'endpoint') && monoClass,
        className,
      )}
    >
      {children}
    </Badge>
  )
}
